import React, { useEffect, useState } from 'react';
import { dataService } from '../../services/dataService';

interface UserEngagementMetricsProps {
  dateRange: 'week' | 'month' | 'quarter' | 'year';
  districtId: string;
}

interface EngagementData {
  category: string;
  dailyActiveUsers: number;
  averageSessionTime: number; // in minutes
  completionRate: number; // percentage
  previousPeriodChange: number; // percentage change from previous period
}

const UserEngagementMetrics: React.FC<UserEngagementMetricsProps> = ({ dateRange, districtId }) => {
  const [loading, setLoading] = useState(true);
  const [engagementData, setEngagementData] = useState<EngagementData[]>([]);
  const [totalEngagement, setTotalEngagement] = useState<{
    totalUsers: number;
    averageSessionTime: number;
    completionRate: number;
    previousPeriodChange: number;
  }>({
    totalUsers: 0,
    averageSessionTime: 0,
    completionRate: 0,
    previousPeriodChange: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // In a real implementation, this would call an API with the dateRange and districtId parameters
        // For now, we'll generate mock data based on the parameters
        const mockData = generateMockData(dateRange, districtId);
        setEngagementData(mockData);
        
        // Calculate total engagement metrics
        const totalUsers = mockData.reduce((sum, data) => sum + data.dailyActiveUsers, 0);
        const averageSessionTime = mockData.reduce((sum, data) => sum + (data.averageSessionTime * data.dailyActiveUsers), 0) / totalUsers;
        const completionRate = mockData.reduce((sum, data) => sum + (data.completionRate * data.dailyActiveUsers), 0) / totalUsers;
        const previousPeriodChange = mockData.reduce((sum, data) => sum + (data.previousPeriodChange * data.dailyActiveUsers), 0) / totalUsers;
        
        setTotalEngagement({
          totalUsers,
          averageSessionTime,
          completionRate,
          previousPeriodChange
        });
      } catch (error) {
        console.error('Error fetching user engagement data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateRange, districtId]);

  const generateMockData = (range: string, district: string): EngagementData[] => {
    const categories = ['Students', 'Teachers', 'School Admins'];
    const result: EngagementData[] = [];
    
    // Base values that will be adjusted based on district, range, and user category
    const baseActiveUsers = district === 'all' ? 5000 : 1500;
    const baseSessionTime = 25; // minutes
    const baseCompletionRate = 65; // percentage
    const basePeriodChange = 5; // percentage
    
    // Factors for each category (students have more users but less session time, etc.)
    const categoryFactors = {
      'Students': { users: 0.9, sessionTime: 0.8, completionRate: 0.9, periodChange: 1.2 },
      'Teachers': { users: 0.08, sessionTime: 1.5, completionRate: 1.2, periodChange: 0.8 },
      'School Admins': { users: 0.02, sessionTime: 2.0, completionRate: 1.3, periodChange: 0.5 }
    };
    
    // Range factors (longer ranges have more users but less average engagement)
    const rangeFactor = range === 'week' ? 1 : 
                        range === 'month' ? 1.2 : 
                        range === 'quarter' ? 1.5 : 2;
    
    // Generate data for each category
    for (const category of categories) {
      const factors = categoryFactors[category as keyof typeof categoryFactors];
      
      // Calculate values with some randomness
      const dailyActiveUsers = Math.round(baseActiveUsers * factors.users * rangeFactor * (0.9 + Math.random() * 0.2));
      const averageSessionTime = Math.round(baseSessionTime * factors.sessionTime * (0.9 + Math.random() * 0.2));
      const completionRate = Math.min(100, Math.round(baseCompletionRate * factors.completionRate * (0.9 + Math.random() * 0.2)));
      const previousPeriodChange = Math.round(basePeriodChange * factors.periodChange * (0.8 + Math.random() * 0.4) * 10) / 10;
      
      result.push({
        category,
        dailyActiveUsers,
        averageSessionTime,
        completionRate,
        previousPeriodChange
      });
    }
    
    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Total Active Users</div>
            <div className="text-2xl font-bold text-gray-800">
              {totalEngagement.totalUsers.toLocaleString()}
            </div>
            <div className={`text-xs mt-1 ${totalEngagement.previousPeriodChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {totalEngagement.previousPeriodChange >= 0 ? '↑' : '↓'} {Math.abs(totalEngagement.previousPeriodChange).toFixed(1)}% from previous {dateRange}
            </div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Avg. Session Duration</div>
            <div className="text-2xl font-bold text-gray-800">
              {Math.round(totalEngagement.averageSessionTime)} min
            </div>
            <div className="text-xs mt-1 text-gray-500">
              Per active user
            </div>
          </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User Category
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Active Users
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Avg. Session
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Completion Rate
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Change
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {engagementData.map((data) => (
              <tr key={data.category}>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                  {data.category}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {data.dailyActiveUsers.toLocaleString()}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {data.averageSessionTime} min
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                      <div 
                        className="bg-primary rounded-full h-2" 
                        style={{ width: `${data.completionRate}%` }}
                      ></div>
                    </div>
                    <span>{data.completionRate}%</span>
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${data.previousPeriodChange >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {data.previousPeriodChange >= 0 ? '+' : ''}{data.previousPeriodChange}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="mt-6">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Engagement Insights</h3>
        <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600">
          <ul className="space-y-1">
            <li className="flex items-start">
              <svg className="h-5 w-5 text-primary mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                {dateRange === 'week' ? 'This week' : 
                 dateRange === 'month' ? 'This month' : 
                 dateRange === 'quarter' ? 'This quarter' : 'This year'}, 
                 student engagement has {totalEngagement.previousPeriodChange >= 0 ? 'increased' : 'decreased'} by {Math.abs(totalEngagement.previousPeriodChange).toFixed(1)}%.
              </span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-primary mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                {engagementData.find(d => d.category === 'Teachers')?.averageSessionTime} minutes is the average session time for teachers, {
                  engagementData.find(d => d.category === 'Teachers')?.averageSessionTime! > 
                  engagementData.find(d => d.category === 'Students')?.averageSessionTime! ? 'higher' : 'lower'
                } than students.
              </span>
            </li>
            <li className="flex items-start">
              <svg className="h-5 w-5 text-primary mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                The overall completion rate of {totalEngagement.completionRate.toFixed(1)}% indicates {
                  totalEngagement.completionRate > 75 ? 'strong' : 
                  totalEngagement.completionRate > 50 ? 'moderate' : 'low'
                } user engagement across the platform.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UserEngagementMetrics;
